import _ from 'underscore';
import { Message } from 'botframework-directlinejs';
import {
  type IStructuredLogMessage,
  type TCreateStructuredLogMessage,
} from '../types/adscopilot-structured-log-types';
import {
  type ICreateStructuredLogParams,
  type IStructuredLogActivityValue,
  type IStructuredLogIntentData,
  type TStructuredLogFlowTransitionActivity,
} from '../types/structured-log-types';
import { createStructuredLogBase } from './create-structured-log-base';

const FLOW_TRANSITION_ACTIVITY_VALUES: ReadonlySet<string> = new Set([
  'ConversationResume',
  'ConnectToAgent',
  'chatHistoryResume',
]);

const getStructuredLogLiveAgentMessage = (activity: Message): Pick<IStructuredLogMessage, 'activityType' | 'activityText' | 'additionalContext'> => {
  if (activity.text === 'agentassignmentready') {
    return {
      activityType: 'LiveAgentAssigned',
      activityText: 'Connecting to assigned agent.',
    };
  }

  if (activity.text === 'agentaccepted') {
    return {
      activityType: 'LiveAgentAccepted',
      activityText: 'Agent accepted to join the conversation.',
    };
  }

  if (activity.text === 'agentendconversation') {
    return {
      activityType: 'LiveAgentConversationEnded',
      activityText: 'Live agent conversation ended.',
    };
  }

  if (activity.text === 'agentpostchatsurvey') {
    return {
      activityType: 'LiveAgentPostChatSurveyDisplayed',
      activityText: 'Live agent post chat survey displayed to user.',
    };
  }

  return {
    activityType: 'LiveAgentMessage',
    activityText: activity.text as string,
    additionalContext: {
      liveAgentConversationId: activity.conversation?.id,
    },
  };
};

const getStructuredLogTestYourTagToolMessage = (activity: ICreateStructuredLogParams['activity']): Pick<IStructuredLogMessage, 'activityType' | 'siteLinks' | 'learnMoreLinks' | 'questionSuggestions' | 'additionalContext'> => ({
  activityType: 'TestYourTagTool',
  siteLinks: activity.value?.SiteLinks,
  learnMoreLinks: activity.value?.Sources,
  questionSuggestions: activity.value?.QuestionSuggestions,
  additionalContext: {
    isHiddenFromUser: activity.value?.isHiddenFromUser,
    intentData: activity.value?.IntentData,
  },
});

const getStructuredLogStaticMessage = (activity: Message): Pick<IStructuredLogMessage, 'activityType' | 'activityText' | 'additionalContext'> => {
  const getStaticMessageActivityText = () => {
    switch (activity.value as unknown as string) {
      case 'ResponsibleAI':
      case 'SydneyResponsibleAI':
      case 'OutofScope':
      case 'InternalError':
      case 'InvalidPayload':
      case 'LargePayload':
      case 'MissingArgumentsInPayload':
      case 'StreamingCancelled':
      case 'EmptyReplyToIdToCancelStreaming':
      case 'NoDataAvailable':
      case 'StreamingCompleted':
      case 'Timeout':
        return `Copilot unable to help. Reason: ${activity.value}`;
      case 'TokenExpiredAutoReload':
      case 'TokenExpired':
        return `Copilot token expired. Restarted with new session. Reason: ${activity.value}`;
      case 'TurnLimitReached':
        return 'Copilot chat limit reached. Restarted with new session.';
      case 'SignInError':
        return 'Copilot sign-in error.';
      case 'HandoffMessage':
        return 'User requested with query description to initiate chat with Microsoft Advertising Expert.';
      default:
        return activity.value as unknown as string;
    }
  };

  return {
    activityType: 'Message',
    activityText: getStaticMessageActivityText(),
    additionalContext: {
      activityValue: activity.value,
    },
  };
};

const isFlowTransitionActivityType = (activity: ICreateStructuredLogParams['activity']): boolean => (
  activity.type === 'message' && typeof activity.value === 'string' && FLOW_TRANSITION_ACTIVITY_VALUES.has(activity.value as unknown as string)
);

const getStructuredLogFlowTransitionActivity = (activity: ICreateStructuredLogParams['activity']): Pick<IStructuredLogMessage, 'activityType' | 'activityText' | 'additionalContext'> => {
  const flowTransitionActivity = activity as TStructuredLogFlowTransitionActivity;

  switch (activity.value) {
    case 'ConversationResume':
      return {
        activityType: 'ConversationResumedWithCopilot',
        activityText: 'Conversation resumed with Copilot.',
        additionalContext: {
          activityValue: activity.value,
        },
      };
    case 'ConnectToAgent':
      return {
        activityType: 'LiveAgentQueueDisplayed',
        activityText: 'Live agent queue displayed to connect to agent.',
        additionalContext: {
          activityValue: `value: ${activity.value}; valueType: ${flowTransitionActivity.valueType}; liveAgentData: ${flowTransitionActivity.liveAgentData?.reason || flowTransitionActivity.liveAgentData?.Reason}`,
        },
      };
    case 'chatHistoryResume':
      return {
        activityType: 'chatHistoryResume',
        activityText: 'User resuming the older conversation from Chat History',
        additionalContext: {
          activityValue: activity.value,
        },
      };
    default:
      return {
        activityType: 'Message',
        activityText: `Unknown special activity type: ${activity.value}`,
        additionalContext: {
          activityValue: activity.value,
        },
      };
  }
};

const getStructuredLogConversationMessage = (activity: ICreateStructuredLogParams['activity']): Pick<IStructuredLogMessage, 'activityType' | 'siteLinks' | 'learnMoreLinks' | 'questionSuggestions' | 'additionalContext'> => ({
  activityType: 'Message',
  siteLinks: activity.value?.SiteLinks,
  learnMoreLinks: activity.value?.Sources,
  questionSuggestions: activity.value?.QuestionSuggestions,
  additionalContext: {
    isHiddenFromUser: activity.value?.isHiddenFromUser,
    intentData: activity.value?.IntentData,
  },
});

export const createStructuredLogMessage: TCreateStructuredLogMessage = (params) => {
  const { activity } = params;
  const activityValue = activity.value as IStructuredLogActivityValue | undefined;

  const structuredLogBase = createStructuredLogBase(params);

  // This is a special case for transition activities like 'ConversationResume' or 'ConnectToAgent'
  if (isFlowTransitionActivityType(activity)) {
    const structuredLogFlowTransitionActivity = getStructuredLogFlowTransitionActivity(activity);
    return {
      ...structuredLogBase,
      ...structuredLogFlowTransitionActivity,
      additionalContext: {
        ...structuredLogBase.additionalContext,
        ...structuredLogFlowTransitionActivity.additionalContext,
      },
    };
  }

  if (activity.type === 'message' && activity.value as unknown === 'LiveAgentMessage') {
    const structuredLogLiveAgentMessage = getStructuredLogLiveAgentMessage(activity);
    return {
      ...structuredLogBase,
      ...structuredLogLiveAgentMessage,
      additionalContext: {
        ...structuredLogBase.additionalContext,
        ...structuredLogLiveAgentMessage.additionalContext,
      },
    };
  }

  if (activity.type === 'message' && activity.text as unknown === 'StaticMessages') {
    const structuredLogStaticMessage = getStructuredLogStaticMessage(activity);
    return {
      ...structuredLogBase,
      ...structuredLogStaticMessage,
      additionalContext: {
        ...structuredLogBase.additionalContext,
        ...structuredLogStaticMessage.additionalContext,
      },
    };
  }

  if (activity.type === 'message' && _.any(activityValue?.IntentData as IStructuredLogIntentData[], (intent: IStructuredLogIntentData) => intent.IntentType === 'TestYourTagTool')) {
    const structuredLogStaticMessage = getStructuredLogTestYourTagToolMessage(activity);
    return {
      ...structuredLogBase,
      ...structuredLogStaticMessage,
      additionalContext: {
        ...structuredLogBase.additionalContext,
        ...structuredLogStaticMessage.additionalContext,
      },
    };
  }

  const structuredLogConversationMessage = getStructuredLogConversationMessage(activity);
  return {
    ...structuredLogBase,
    ...structuredLogConversationMessage,
    additionalContext: {
      ...structuredLogBase.additionalContext,
      ...structuredLogConversationMessage.additionalContext,
    },
  };
};
